import { observable, action, computed } from "mobx";
import fetch from "unfetch";
import { scaleQuantile } from "@vx/scale";
//@ts-ignore
import log from "../log.json";
import Land from "./Land";
import Provincie from "./Provincie";
import Gemeente from "./Gemeente";
import Day from "./Day";
import { addDays } from "date-fns";
import VeiligheidsRegio from "./VeiligheidsRegio";
import Base from "./Base";
import PaintStore from "./paint/PaintStore";
import DataSet from "./DataSet";

export type View = "gemeente" | "provincie" | "regio";
export type Stat = {
  total: number;
  totalP: number;
  delta: number;
  deltaP: number;
};

export type ViewOption = {
  view: View;
  name: string;
};

export default class Store {
  @observable ready: boolean = false;
  @observable activeIndex: number;
  @observable view: View = "gemeente";
  @observable activeGemeente: Gemeente | null = null;
  @observable activeProvincie: Provincie | null = null;
  @observable playing: boolean = false;
  @observable asIncrement: boolean = false;
  @observable as100k: boolean = true;
  @observable askm2: boolean = false;
  @observable activeBase: Base = null;

  land: Land;
  provincies: Provincie[] = [];
  gemeentes: Gemeente[] = [];
  veiligheidsRegios: VeiligheidsRegio[] = [];
  day0: Date;
  dayCount: number = 0;
  totals: DataSet;
  deaths: DataSet;
  ic: DataSet;

  paint: PaintStore;

  viewOptions: ViewOption[] = [
    {
      view: "gemeente",
      name: "Per gemeente"
    },
    {
      view: "provincie",
      name: "Per provincie"
    },
    {
      view: "regio",
      name: "Per Veiligheidsregio"
    }
  ];

  constructor() {
    this.activeIndex = log.dayCount - 1;
    this.day0 = new Date(log.day0);
    this.dayCount = log.dayCount;
    this.land = new Land(this);
    this.totals = new DataSet(this, log.totals);
    this.deaths = new DataSet(this, log.deaths, true);
    this.ic = new DataSet(this, log.ic);
    this.paint = new PaintStore(this);
  }

  setActiveGemeenteById(id: string) {
    if (this.activeBase && this.activeBase.id == id) {
      this.activeBase = null;
      return;
    }
    let gemeente = this.gemeentes.find(g => g.id == id);

    if (gemeente) {
      this.activeBase = gemeente;
    }
  }

  setActiveProvincieById(id: string) {
    if (this.activeBase && this.activeBase.id == id) {
      this.activeBase = null;
      return;
    }
    let provincie = this.provincies.find(g => g.id == id);

    if (provincie) {
      this.activeBase = provincie;
    }
  }

  setActiveRegioById(id: string) {
    if (this.activeBase && this.activeBase.id == id) {
      this.activeBase = null;
      return;
    }
    let regio = this.veiligheidsRegios.find(g => g.id == id);

    if (regio) {
      this.activeBase = regio;
    }
  }

  @action
  async loadData() {
    const provincies = await fetch("/provincies.json").then(r => r.json());
    this.provincies = provincies.features.map((f: any) => {
      let p = new Provincie(this, f);
      this.land.provincies.push(p);
      return p;
    });
    const regios = await fetch("/veiligheidsregios.json").then(r => r.json());
    this.veiligheidsRegios = regios.features.map((f: any) => {
      let p = new VeiligheidsRegio(this, f);
      return p;
    });

    const gemeentes = await fetch("/gemeentes.json").then(r => r.json());

    gemeentes.features.forEach((f: any) => {
      let gemeente = new Gemeente(this, f);
      this.gemeentes.push(gemeente);

      let provincie = this.provincies.find(p => p.id === gemeente.provincieId);
      if (provincie) provincie.gemeentes.push(gemeente);

      let regio = this.veiligheidsRegios.find(p => p.id === gemeente.regioId);
      if (regio) {
        regio.gemeentes.push(gemeente);
      }
    });

    const data = await fetch("/data.json").then(r => r.json());
    Object.entries(data).forEach(([key, value]) => {
      let gemeente = this.gemeentes.find(g => g.id === key);
      if (gemeente) gemeente.calc(value as number[]);
    });

    this.provincies.forEach(p => p.update());
    this.veiligheidsRegios.forEach(p => p.update());
    this.land.update();

    this.ready = true;
  }

  @computed get days(): Day[] {
    let result: Day[] = [];
    let t = this.land.totals;
    for (let i = 0; i < t.length; i++) {
      let delta = i > 0 ? t[i] - t[i - 1] : 0;
      result.push(
        new Day(i, addDays(this.day0, i), t[i], delta, this.deaths.data[i], this.ic.data[i], this.totals.data[i])
      );
    }

    return result;
  }

  @computed get minMax() {
    let globalMin = Infinity;
    let globalMax = 0;

    let trg = this.view === "gemeente" ? this.gemeentes : this.provincies;

    trg.forEach(g => {
      let { min, max } = g.minMax;
      if (min < globalMin) globalMin = min;
      if (max > globalMax) globalMax = max;
    });

    return { min: globalMin, max: globalMax };
  }

  @computed get geoJSON() {
    let features = [];
    if (this.view === "gemeente") {
      this.gemeentes.forEach(g => {
        if (g.geometry !== null) {
          features.push(g.feature);
        }
      });
    } else if (this.view === "provincie") {
      this.provincies.forEach(p => {
        features.push(p.feature);
      });
    } else {
      this.veiligheidsRegios.forEach(p => {
        features.push(p.feature);
      });
    }
    return {
      type: "FeatureCollection",
      name: this.view === "gemeente" ? "Gemeentes" : "Provincies",
      crs: {
        type: "name",
        properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" }
      },
      features: features
    };
  }

  @computed get death() {
    return this.deaths[this.activeIndex];
  }

  @computed get deathsTotal() {
    return this.deaths.slice(0, this.activeIndex + 1).reduce((acc, cur) => {
      return acc + cur;
    }, 0);
  }

  @computed get deathP() {
    let i = this.activeIndex;
    if (i > 0) {
      let v = this.deaths[i] / this.deaths[i - 1] - 1;
      if (v === Infinity) return 0;
      return Math.round(v * 100);
    }
    return 0;
  }

  nextView() {
    let v = this.viewIndex < this.viewOptions.length - 1 ? this.viewIndex + 1 : 0;
    this.view = this.viewOptions[v].view;
  }

  prevView() {
    let v = this.viewIndex > 0 ? this.viewIndex - 1 : this.viewOptions.length - 1;
    this.view = this.viewOptions[v].view;
  }

  @computed get viewIndex() {
    let f = this.viewOptions.findIndex(v => v.view === this.view);
    if (f >= 0) return f;
    return 0;
  }

  @computed get target(): Base[] {
    switch (this.view) {
      case "regio":
        return this.veiligheidsRegios;
      case "provincie":
        return this.provincies;
      default:
        return this.gemeentes;
    }
  }

  @computed get targetProperty(): string {
    if (this.asIncrement) {
      return this.as100k ? "delta100k" : "delta";
    }
    return this.as100k ? "total100k" : "total";
  }
}
